import { useEffect, useRef, useState } from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  toast,
} from "@coval/ui"

import { logger } from "@/lib/logger"
import { CreateResourceButton } from "@/components/ResourceActionButton"
import { getTimezoneAbbreviation, getUserTimezone } from "@/utils/date/timezone"

import { useScheduleForm } from "../hooks/useScheduleForm"
import {
  useCreateScheduledRunTransport,
  useScheduledRunsTransportInvalidation,
} from "../routes/useScheduledRunsTransport"
import { formatScheduleSummaryFromState } from "../utils/scheduleStateUtils"
import { ScheduleFormFields } from "./ScheduleFormFields"

const log = logger("ScheduleTemplateDialog")

interface ScheduleTemplateDialogProps {
  templateId: string
  templateName: string
  onClose: () => void
}

export function ScheduleTemplateDialog({
  templateId,
  templateName,
  onClose,
}: ScheduleTemplateDialogProps) {
  const form = useScheduleForm()
  const mutation = useCreateScheduledRunTransport()
  const { invalidateList } = useScheduledRunsTransportInvalidation()
  const [scheduleName, setScheduleName] = useState("")
  const [isPending, setIsPending] = useState(false)
  const [attempted, setAttempted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const inFlight = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const timezone = getUserTimezone()
  const timezoneAbbr = getTimezoneAbbreviation(timezone)
  const summary = formatScheduleSummaryFromState(form.state, timezoneAbbr)
  const nameError = !scheduleName.trim()
    ? "Enter a schedule name."
    : scheduleName.trim().length > 200
      ? "Use a name of 200 characters or fewer."
      : undefined
  const scheduleError = !form.validation.isFormValid
    ? "Choose a valid interval or time and days for this schedule."
    : undefined
  const validationIssues = [nameError, scheduleError].filter(
    (issue): issue is string => Boolean(issue),
  )

  const handleSubmit = async () => {
    setAttempted(true)
    if (inFlight.current || validationIssues.length || !templateId) {
      return
    }
    inFlight.current = true
    setIsPending(true)
    setErrorMessage(null)
    try {
      await mutation.mutateAsync({
        display_name: scheduleName.trim(),
        run_template_id: templateId,
        schedule_expression: form.expression,
        schedule_timezone: timezone,
        enabled: true,
      })
      // A cache refresh failure must not turn a successful creation into a retry.
      void invalidateList().catch((error) =>
        log.error("Could not refresh scheduled runs", error),
      )
      if (!mounted.current) {
        return
      }
      toast.success(`Scheduled run "${scheduleName.trim()}" created`)
      onClose()
    } catch (error) {
      if (mounted.current) {
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Could not create scheduled run. Try again.",
        )
      }
    } finally {
      inFlight.current = false
      if (mounted.current) {
        setIsPending(false)
      }
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !inFlight.current) {
          onClose()
        }
      }}
    >
      <DialogContent
        size="lg"
        className="max-h-[85vh] max-w-3xl overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Schedule Run</DialogTitle>
          <DialogDescription>
            Schedule the saved configuration of {templateName}.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <fieldset disabled={isPending} className="min-w-0 space-y-4">
            <FormField
              label="Schedule name"
              error={attempted ? nameError : undefined}
            >
              {(ids) => (
                <Input
                  {...ids}
                  value={scheduleName}
                  maxLength={200}
                  placeholder="e.g. Nightly Regression"
                  onChange={(event) => setScheduleName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      void handleSubmit()
                    }
                  }}
                />
              )}
            </FormField>
            <ScheduleFormFields
              actions={form.actions}
              state={form.state}
              timezoneAbbr={timezoneAbbr}
              validation={form.validation}
            />
          </fieldset>
          {attempted && scheduleError && (
            <p role="alert" className="mt-3 text-sm text-coval-danger-text">
              {scheduleError}
            </p>
          )}
          {errorMessage && (
            <p role="alert" className="mt-3 text-sm text-coval-danger-text">
              {errorMessage}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          {form.validation.isFormValid && summary && (
            <p className="mr-auto text-xs text-coval-text-muted" role="status">
              {summary}
            </p>
          )}
          <Button variant="ghost" disabled={isPending} onClick={onClose}>
            Cancel
          </Button>
          <CreateResourceButton
            resourceName="Scheduled Run"
            loading={isPending}
            validationIssues={validationIssues}
            onValidationAttempt={() => setAttempted(true)}
            onClick={() => void handleSubmit()}
            data-testid="template-schedule-confirm-button"
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
